<template>
  <div class="metric-page">
    <CRow>
      <CCol lg="6" class="mb-4">
        <CCard class="metric-card">
          <CCardBody>
            <div class="metric-card__header">
              <div>
                <div class="metric-card__eyebrow">Zadnjih 7 dana</div>
                <h3 class="metric-card__title">Dnevni unos</h3>
              </div>
              <div class="metric-card__stats">
                <div>
                  <span class="metric-card__stats-label">Danas</span>
                  <strong>{{ todayCalories }}</strong>
                </div>
                <div>
                  <span class="metric-card__stats-label">Prosjek</span>
                  <strong>{{ averageCaloriesLastWeek }}</strong>
                </div>
              </div>
            </div>
            <div class="metric-card__chart">
              <CChartBar class="metric-card__chart-canvas" :datasets="lastWeekChart.datasets" :options="chartOptions" :labels="lastWeekChart.labels" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6" class="mb-4">
        <CCard class="metric-card">
          <CCardBody>
            <div class="metric-card__header">
              <div>
                <div class="metric-card__eyebrow">Povijest</div>
                <h3 class="metric-card__title">Svi zapisi</h3>
              </div>
              <div class="metric-card__stats">
                <div>
                  <span class="metric-card__stats-label">Limit</span>
                  <strong>{{ maxCalories }} kcal</strong>
                </div>
                <div>
                  <span class="metric-card__stats-label">Prosjek</span>
                  <strong>{{ averageAllCalories }}</strong>
                </div>
              </div>
            </div>
            <div class="metric-card__chart">
              <CChartBar class="metric-card__chart-canvas" :datasets="allTimeChart.datasets" :options="chartOptions" :labels="allTimeChart.labels" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <MetricHistoryTable :entries="$store.state.calories" value-title="Hrana" unit="kcal" />
  </div>
</template>

<script>
import { CChartBar } from "@coreui/vue-chartjs";
import moment from "moment";
import MetricHistoryTable from "./MetricHistoryTable";

const currentYear = new Date().getFullYear();
const getChartXAxisDateFormat = (date) => moment(date).format("DD.MM" + (moment(date).format("YYYY") == currentYear ? "" : ".YY"));

const getDaysBetweenDates = (from, to) => {
  const dates = [];
  while (from.isSameOrBefore(to)) {
    dates.push(from.toISOString().substring(0, 10));
    from = moment(from).add(1, "days");
  }
  return dates;
};

const buildCaloriesTimeline = (caloriesData) => {
  if (!caloriesData.length) {
    return [];
  }

  const fromDate = moment(caloriesData[0].date);
  const toDate = moment().add(1, "days");

  const caloriesByDate = caloriesData.reduce((result, entry) => {
    result[moment(entry.date).format("YYYY-MM-DD")] = entry.value;
    return result;
  }, {});

  return getDaysBetweenDates(fromDate, toDate).map((date) => {
    return {
      date,
      value: caloriesByDate[date] ?? 0,
    };
  });
};

const getAverage = (entries) => entries.map((entry) => entry.value).reduce((sum, value) => sum + value, 0) / entries.filter((entry) => entry.value > 0).length || 0;

const buildBarDataset = (entries, threshold) => ({
  labels: entries.map((entry) => getChartXAxisDateFormat(entry.date)),
  datasets: [
    {
      label: "Calories",
      backgroundColor: entries.map((entry) => (entry.value > threshold ? "#dc2626" : "#0f766e")),
      borderRadius: 6,
      barPercentage: 0.72,
      categoryPercentage: 0.74,
      data: entries.map((entry) => entry.value),
    },
    {
      type: "line",
      label: "Limit",
      data: entries.map(() => threshold),
      borderColor: "#d97706",
      borderDash: [6, 6],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
    },
  ],
});

export default {
  components: { CChartBar, MetricHistoryTable },
  data() {
    return {
      todayCalories: "0 kcal",
      averageCaloriesLastWeek: "0 kcal",
      averageAllCalories: "0 kcal",
      allTimeChart: { labels: [], datasets: [] },
      lastWeekChart: { labels: [], datasets: [] },
    };
  },
  computed: {
    maxCalories() {
      return this.$store.state.maxCalories;
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#0f172a",
          titleFontColor: "#f8fafc",
          bodyFontColor: "#e2e8f0",
          xPadding: 12,
          yPadding: 10,
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              return dataset.type === "line" ? `Limit: ${tooltipItem.value} kcal` : `Calories: ${tooltipItem.value} kcal`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false, drawBorder: false },
              ticks: { fontColor: "#94a3b8", maxTicksLimit: 8 },
            },
          ],
          yAxes: [
            {
              gridLines: { color: "#e8eef5", drawBorder: false },
              ticks: {
                min: 0,
                maxTicksLimit: 6,
                fontColor: "#94a3b8",
                callback: (value) => `${value} kcal`,
              },
            },
          ],
        },
      };
    },
  },
  mounted() {
    const calories = buildCaloriesTimeline(this.$store.state.calories);
    const todayEntry = calories[calories.length - 1] || { value: 0 };
    const historicalCalories = calories.slice(0, -1);
    const lastWeekCalories = historicalCalories.slice(Math.max(historicalCalories.length - 7, 0));

    this.todayCalories = `${todayEntry.value} kcal`;
    this.averageAllCalories = `${parseInt(getAverage(historicalCalories), 10) || 0} kcal`;
    this.averageCaloriesLastWeek = `${parseInt(getAverage(lastWeekCalories), 10) || 0} kcal`;
    this.allTimeChart = buildBarDataset(historicalCalories, this.maxCalories);
    this.lastWeekChart = buildBarDataset(lastWeekCalories, this.maxCalories);
  },
};
</script>

<style scoped>
.metric-page {
  display: grid;
  gap: 1.5rem;
}

.metric-card {
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
}

.metric-card .card-body {
  display: flex;
  flex-direction: column;
  min-height: 380px;
  padding: 1.5rem;
}

.metric-card__chart {
  position: relative;
  flex: 0 0 auto;
  height: clamp(220px, 28vh, 260px);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.metric-card__chart-canvas {
  height: 100%;
  width: 100%;
  min-width: 0;
}

.metric-card__chart-canvas /deep/ canvas {
  height: 100% !important;
  width: 100% !important;
  max-width: 100%;
  display: block;
}

.metric-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric-card__eyebrow {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-card__title {
  margin: 0.35rem 0 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 700;
}

.metric-card__stats {
  display: flex;
  gap: 1.5rem;
  text-align: right;
}

.metric-card__stats-label {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.metric-card__stats strong {
  color: #0f172a;
  font-size: 1.05rem;
  line-height: 1.2;
}

@media (max-width: 767px) {
  .metric-card {
    border-radius: 14px;
  }

  .metric-card .card-body {
    min-height: 340px;
    padding: 1rem;
  }

  .metric-card__chart {
    height: 210px;
  }

  .metric-card__header {
    flex-direction: column;
    margin-bottom: 1rem;
  }

  .metric-card__stats {
    width: 100%;
    justify-content: space-between;
    text-align: left;
  }

  .metric-card__title {
    font-size: 1.05rem;
  }
}
</style>
